
import React, { useRef, useEffect, useState } from 'react'
import { useParams, useHistory } from "react-router";
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";
import { useCountriesLov, useFetchRequestBrowserData, useProtocolSetupData, useValidateRequest } from 'protocol-setup/redux/useLovs';
import InactiveRequestRedirect from 'protocol-setup/components/InactiveRequestRedirect';
import { getRequestBarContent } from 'protocol-setup/components/RequestActionBar/RequestBarHelper';
import RequestActionBar from 'protocol-setup/components/RequestActionBar';
import { FormIsDirtyAlert } from "protocol-setup/components/FormIsDirtyAlertModal";
import _ from 'lodash';
import { useSetupPermission } from 'protocol-setup/helpers';
import ProtectionElements from 'protocol-setup/helpers/ProtectionElement';
import InfoForm from './InfoForm';
import RegulatorySetupForm from './RegulatorySetupForm';
import SignOffForms from './SignOffForms';

const softValidationFields = [
  'requestDate',
  'requestType',
  'protSetupContacts',
  'protocolVersionDt',
  'protocolVersionNumber',
  'releaseDt'
];

function RegulatorySetup() {
  const { rqNum } = useParams();
  const history = useHistory()
  const [isTabDirty, setDirtyTab] = useState(false);
  const [openModal, setModal] = useState(false);
  const lastFocusedElement = useRef(null);
  const { data: protocolSetupData, isLoading, trigger: fetchPsData } = useProtocolSetupData(rqNum, 'b', history);
  const { countryList } = useCountriesLov();
  const [enabledTabs, setEnabledTabs] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [hasCompletedRequest, setHasCompletedRequest] = useState(false);
  const { requestStatus, protocolNumber, requestNumber, requestType } = protocolSetupData || {};
  const [liveFormData, setLiveFormData] = useState(null);


  const { isSubmitFormEnabled } = useValidateRequest(liveFormData, "REGULATORY", softValidationFields);

  const [isFormReadOnly, setIsFormReadOnly] = useState(false);
  const [requestBarContent, setRequestBarContent] = useState([]);

  const { requestBrowserData } = useFetchRequestBrowserData();

  const canSignoffView = useSetupPermission(ProtectionElements.SIGNOFF_VIEW);
  const canEditRegulatoryRequest = useSetupPermission(ProtectionElements.REGULATORY_REQUEST);


  useEffect(() => {
    if (protocolSetupData) setLiveFormData(protocolSetupData);
  }, [protocolSetupData]);


  useEffect(() => {
    if (!isLoading) {
      if (requestNumber) {
        setEnabledTabs(!!requestNumber && !!requestType);
        setTabIndex(0);

        const status = (requestStatus || "").toUpperCase();
        if (status && !["NEW", "DRAFT"].includes(status)) {
          setIsFormReadOnly(true);
        } else {
          setIsFormReadOnly(false);
        }
        setRequestBarContent(getRequestBarContent({ status, requestNumber, obj: protocolSetupData }))
      } else if (protocolNumber) {
        setTabIndex(0);
      } else {
        history.push("/protocol-setup/browser");
      }
    }

  }, [requestNumber, requestStatus, protocolNumber, isLoading, requestType]);


  useEffect(() => {
    if (requestBrowserData?.data && protocolNumber) {
      const filterData = _.filter(requestBrowserData?.data, { protocolNumber, formPartType: "Regulatory", requestStatus: "COMPLETED" });
      setHasCompletedRequest(filterData.length > 0)
    }
  }, [requestBrowserData, protocolNumber]);

  const doTabAction = (index) => {
    if (!isTabDirty) {
      setTabIndex(index);
    } else {

      lastFocusedElement.current = document.activeElement !== document.body
        ? document.activeElement
        : document.querySelector(`[role="tab"]:nth-child(${tabIndex + 1})`);
      setModal(true);
    }
  }

  if (rqNum && isLoading) {
    return null
  }

  const onSuccess = async () => {
    await fetchPsData();
  };

  return (
    <div className="card-wrapper MuiPaper-elevation1 px-4 py-4">
      <InactiveRequestRedirect requestStatus={requestStatus} />
      <h1 className=" font-title color-blue font-bold mt-0">
        {(requestNumber ? `Protocol ${protocolNumber} Setup Request ${requestNumber}` : `New Protocol ${protocolNumber} Setup Request`)}
      </h1>
      <div className='mt-1'>
        Regulatory Setup forms initiate the regulatory setup of a new protocol in CTSU applications or to provide regulatory updates associated with an amendment.
        <br />
        All required fields must be completed to save each page.
      </div>
      <div className="align-center my-3">
        <div className="d-inline-flex align-items-center justify-content-center bg-white shadow-sm round py-1 px-3" style={{ minWidth: '10rem' }}>
          <RequestActionBar
            content={requestBarContent || []}
            isSubmitFormEnabled={isSubmitFormEnabled}
            formPartType="REGULATORY"
            history={history}
            fetchPsData={fetchPsData}
            requestNumber={requestNumber}
            onSuccess={onSuccess}
            protocolNumber={protocolNumber}
            lpo={protocolSetupData?.lpo}
            requestStatus={requestStatus}
            hasEditPermission={canEditRegulatoryRequest}
          />
        </div>
      </div>
      <Tabs
        className="ctsuTabs-wrapper"
        forceRenderTabPanel
        selectedIndex={tabIndex}
        onSelect={doTabAction}
      >
        <TabList className="ctsuTabs-tabs">
          <Tab className="ctsuTabs-tab">Protocol Information</Tab>
          <Tab className="ctsuTabs-tab" disabled={!enabledTabs}>Regulatory Setup</Tab>
        </TabList>
        <TabPanel className="ctsuTabs-tabPanel">
          <div
            className={`ctsuTabs-content ${tabIndex === 0 ? "active" : "d-none"}`}
          >
            <InfoForm
              selectedRequest={protocolSetupData}
              requestNumber={requestNumber}
              isTabDirty={isTabDirty}
              setDirtyTab={setDirtyTab}
              isFormReadOnly={isFormReadOnly || !canEditRegulatoryRequest}
              fetchPsData={fetchPsData}
              isSubmitFormEnabled={isSubmitFormEnabled}
            />
          </div>
        </TabPanel>
        <TabPanel className="ctsuTabs-tabPanel">
          <div
            className={`ctsuTabs-content ${tabIndex === 1 ? "active" : "d-none"}`}
          >
            <RegulatorySetupForm
              selectedRequest={protocolSetupData}
              requestNumber={requestNumber}
              countryList={countryList}
              hasCompletedRequest={hasCompletedRequest}
              isTabDirty={isTabDirty}
              setDirtyTab={setDirtyTab}
              isFormReadOnly={isFormReadOnly || !canEditRegulatoryRequest}
              fetchPsData={fetchPsData}
              isSubmitFormEnabled={isSubmitFormEnabled}
            />
          </div>
        </TabPanel>
      </Tabs>
      {canSignoffView && isFormReadOnly && <SignOffForms protocolSetupData={protocolSetupData} fetchPsData={fetchPsData} />}
      {openModal && <FormIsDirtyAlert setModal={setModal} onClose={() => {
        if (lastFocusedElement.current) {
          setTimeout(() => {
            lastFocusedElement.current.focus();
          }, 0);
        }
      }} />}
    </div>
  );
}

export default RegulatorySetup;
